import React from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import App from './App';
import './index.css';

// ===== Crash Loop Recovery (v3) =====
// NotFoundError (insertBefore/removeChild) durante React commit phase.
// React tenta retry via microtask → loop infinito.
// Fix: 1) sessionStorage counter rompe redirect loop,
//      2) document.open() kill ALL JavaScript state,
//      3) static fallback dopo N redirect falliti.

const CRASH_KEY = 'bt_crash_count';
const MAX_REDIRECTS = 2;
const CRASH_WINDOW = 5000;
const MAX_CRASHES = 3;

// Phase 1: Pre-React — break infinite redirect loop
let redirectCount = 0;
try { redirectCount = parseInt(sessionStorage.getItem(CRASH_KEY) || '0', 10); } catch (e) {}

if (redirectCount >= MAX_REDIRECTS) {
  sessionStorage.removeItem(CRASH_KEY);
  document.title = 'Boat Trader';
  document.documentElement.innerHTML = '<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover">' +
    '<style>body{margin:0;font-family:system-ui,-apple-system,sans-serif;background:#050507;color:#fff;min-height:100dvh;display:flex;align-items:center;justify-content:center}' +
    '.c{max-width:380px;text-align:center;padding:32px 24px}' +
    'h1{font-size:20px;font-weight:700;margin:0 0 8px}' +
    'p{color:rgba(255,255,255,0.5);font-size:14px;margin:0 0 24px;line-height:1.5}' +
    'button{padding:14px 36px;background:#06b6d4;color:#fff;border:none;border-radius:12px;font-size:15px;font-weight:600;cursor:pointer;transition:background .2s}' +
    'button:hover{background:#0891b2}' +
    '.sub{color:rgba(255,255,255,0.25);font-size:11px;margin-top:24px}</style></head>' +
    '<body><div class="c"><h1>Boat Trader</h1><p>Caricamento interrotto per un errore. Clicca per riprovare.</p>' +
    '<button onclick="sessionStorage.removeItem(\'' + CRASH_KEY + '\');location.reload()">Riprova</button>' +
    '<div class="sub">Se il problema persiste, chiudi e riapri l\'app.</div></div></body></html>';
  throw new Error('[BT] Crash loop blocked');
}

// Phase 2: Runtime error handler
let crashCount = 0;
let crashTimer: ReturnType<typeof setTimeout> | null = null;
let appRootInstance: ReactDOM.Root | null = null;
let isRedirecting = false;

function nuclearRedirect() {
  if (isRedirecting) return;
  isRedirecting = true;

  try { sessionStorage.setItem(CRASH_KEY, String(redirectCount + 1)); } catch (e) {}

  // Nuclear: document.open() kills ALL DOM, JS state, event listeners, microtasks
  try {
    document.open();
    document.write('<!DOCTYPE html><html><head><meta charset="utf-8"><meta http-equiv="refresh" content="0;url=/?r=' + Date.now() + '"></head><body></body></html>');
    document.close();
    return;
  } catch (e) {}

  // Fallback chain
  if (appRootInstance) {
    try { appRootInstance.unmount(); } catch (e) {}
    appRootInstance = null;
  }
  try { window.stop(); } catch (e) {}
  window.location.hash = '';
  try { window.location.replace('/?r=' + Date.now()); } catch (e) {}
}

// React 19 handles commit-phase errors (NotFoundError/insertBefore/removeChild)
// gracefully without looping. These event listeners are kept as safety nets but
// no longer escalate commit crashes to nuclear redirect.

function cleanupErrorListeners() {
  window.removeEventListener('error', errorHandler);
  window.removeEventListener('unhandledrejection', rejectionHandler);
}

function errorHandler(event: ErrorEvent) {
  const msg = event.error?.message || event.message || '';

  if (msg.includes('AbortError') || msg.includes('Transition was skipped'))
    return;

  if (isRedirecting) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const isCommitCrash = msg.includes('insertBefore') || msg.includes('removeChild') ||
                        msg.includes('NotFoundError') ||
                        msg.includes("Failed to execute 'insertBefore'");
  if (isCommitCrash)
    return;

  crashCount++;
  console.log('[BT] Crash #' + crashCount + ': ' + msg.slice(0, 80));
  if (crashTimer) clearTimeout(crashTimer);
  crashTimer = setTimeout(() => { crashCount = 0; }, CRASH_WINDOW);
  if (crashCount >= MAX_CRASHES) {
    console.log('[BT] Threshold reached, nuclear redirect');
    event.preventDefault();
    event.stopImmediatePropagation();
    nuclearRedirect();
  }
}

function rejectionHandler(event: PromiseRejectionEvent) {
  if (event.reason?.name === 'AbortError' || event.reason?.message?.includes('Transition was skipped'))
    return;
  event.preventDefault();
  console.error('Unhandled promise rejection:', event.reason);
}

window.addEventListener('error', errorHandler);
window.addEventListener('unhandledrejection', rejectionHandler);

// Allow bfcache by removing error listeners before page freeze
window.addEventListener('pagehide', cleanupErrorListeners);
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', rejectionHandler);
  }
});
import './ios-styles.css';
import './mobile-styles.css';
import './desktop-styles.css';
import './desktop-tweaks.css';
import './styles/glassmorphism.css';
import './styles/view-transitions.css';
import './styles/pwa.css';
import './aurora-pwa.css';

// Service Worker registration + cache cleanup handled by force-react.php.
// to ensure proper control over caching and version management.
// Do NOT register here - would cause duplicate registration error #310

// Detect PWA mode - check if running as installed PWA
/* const isPWA = typeof window !== 'undefined' && (
   (window.matchMedia('(display-mode: standalone)')?.matches === true) ||
   (window.navigator as any).standalone === true ||
   (typeof document.referrer === 'string' && document.referrer.includes('android-app://'))
 ); */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60000,
      cacheTime: 300000,
      refetchOnMount: true,
      refetchOnReconnect: true,
      timeout: 30000,
    },
    mutations: {
      retry: 0,
      timeout: 60000,
    },
  },
});

const rootElement = document.getElementById('root');

// Error logging handled at module level (lines 72-112); this wrapper is a no-op.
const ErrorBoundary = ({ children }: { children: React.ReactNode }) => <>{children}</>;

function renderApp() {
  if (!rootElement) return;
  if (!appRootInstance) {
    try {
      appRootInstance = ReactDOM.createRoot(rootElement, {
        onRecoverableError(error, errorInfo) {
          console.warn('[BOAT-TRADER] Recoverable error:', error, errorInfo);
          if (error instanceof DOMException && error.name === 'NotFoundError') {
            return;
          }
        },
      });
    } catch (e: any) {
      console.error('[BOAT-TRADER] Create root crash:', e);
      rootElement.innerHTML = `<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#050507;padding:24px;font-family:system-ui,sans-serif"><div style="max-width:400px;text-align:center"><h1 style="font-size:22px;font-weight:700;color:#fff;margin:0 0 12px">Errore Applicazione</h1><p style="color:rgba(255,255,255,0.5);font-size:14px;margin:0 0 24px;line-height:1.5">${e?.message || 'Errore sconosciuto'}</p><button onclick="location.reload()" style="padding:12px 32px;background:#06b6d4;color:white;border:none;border-radius:12px;font-size:15px;font-weight:600;cursor:pointer">Ricarica</button></div></div>`;
      return;
    }
  }
  try {
    const root = appRootInstance;
    root.render(
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <HashRouter>
            <App />
            <Toaster position="top-right" />
          </HashRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    );
    sessionStorage.removeItem(CRASH_KEY);
  } catch (e: any) {
    console.error('[BOAT-TRADER] Render crash:', e);
    rootElement.innerHTML = `
      <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#fef2f2,#ffedd5);padding:24px;font-family:sans-serif">
        <div style="max-width:400px;background:white;border-radius:16px;padding:32px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,0.1)">
          <h1 style="font-size:24px;font-weight:700;color:#111;margin:0 0 12px">Oops! Qualcosa è andato storto</h1>
          <p style="color:#666;font-size:14px;margin:0 0 16px;line-height:1.5">Errore durante l'avvio dell'applicazione.</p>
          <pre style="background:#f3f4f6;padding:12px;border-radius:8px;font-size:11px;text-align:left;overflow:auto;max-height:200px;color:#333;margin:0 0 16px">${e?.message || 'Errore sconosciuto'}\n\n${e?.stack?.slice(0, 1500) || ''}</pre>
          <button onclick="location.reload()" style="width:100%;padding:12px 24px;background:#dc2626;color:white;border:none;border-radius:8px;font-size:16px;font-weight:600;cursor:pointer">Ricarica la pagina</button>
        </div>
      </div>`;
  }
}

if (rootElement) {
  renderApp();
}
